import { isAxiosError } from 'axios';

import { authService } from '@/services/authService';
import { isJwtExpired } from '@/utils/jwtUtils';

import { emitForceLogout } from './authEvents';
import { clearTokens, getAccessToken, setTokens } from './tokenStore';

/**
 * Coordinates token refresh for both transports (Apollo's errorLink and the axios interceptor)
 * through one single-flight call, so N concurrent 401s trigger exactly one `/auth/refresh`
 * request — matching the backend's single-use refresh-token rotation (ADR-0006): a second,
 * duplicate refresh call would find the token already rotated and fail (replay revoke).
 *
 * The refresh token itself is never read here — for web it is the `HttpOnly` cookie the browser
 * attaches automatically to `/auth/refresh`. A failed refresh (missing/expired/rotated cookie)
 * surfaces as a rejected call, which we translate into a force-logout.
 */
let inFlightRefresh: Promise<string | null> | null = null;

/**
 * A concurrent refresh committed first, so this one lost the optimistic-concurrency check on the user
 * row (HTTP 409). Nothing was rotated by the losing attempt and the `HttpOnly` cookie is untouched —
 * the backend deliberately leaves it in place for exactly this case.
 */
function isConcurrentRefresh(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 409;
}

async function refreshOnce(): Promise<string> {
  const tokens = await authService.refresh();
  setTokens(tokens.accessToken);
  return tokens.accessToken;
}

async function performRefresh(): Promise<string | null> {
  try {
    return await refreshOnce();
  } catch (error) {
    // Single-flighting keeps one TAB to one refresh, but not one SESSION: two tabs that both wake to
    // an expired token race each other with the same cookie, and one of them loses. That loser's
    // session is alive — retrying picks up the winner's rotated cookie, or lands inside the backend's
    // rotation grace window (ADR-0006). Logging the user out here would reintroduce, one layer up,
    // the exact "a duplicated refresh ends every session" bug that window was added to fix.
    //
    // Deliberately narrow: one retry, only for 409. A network error or a 5xx is NOT retried, because
    // those give no evidence the session survived and a blind retry would only delay the logout.
    if (isConcurrentRefresh(error)) {
      try {
        return await refreshOnce();
      } catch {
        // Two losses in a row is no longer a race worth absorbing — fall through and end the session.
      }
    }

    clearTokens();
    emitForceLogout('refresh-failed');
    return null;
  }
}

interface GetValidAccessTokenOptions {
  /** Skip the expiry check and refresh unconditionally — used after a transport already got a 401. */
  forceRefresh?: boolean;
}

/**
 * Returns a token guaranteed not to be expired, refreshing first if needed. Callers (authLink,
 * the axios interceptor) never call `/auth/refresh` directly — always through here.
 */
export async function getValidAccessToken(
  options: GetValidAccessTokenOptions = {},
): Promise<string | null> {
  const current = getAccessToken();

  if (!options.forceRefresh && current && !isJwtExpired(current)) {
    return current;
  }

  // No in-memory token and no explicit forceRefresh means there is nothing indicating a session
  // ever existed on this request — e.g. an anonymous visitor on a public page (the company
  // registration wizard, its email verification step) calling a GraphQL operation that requires
  // no auth in the first place. `AuthProvider`'s own mount-time restore already owns the "maybe
  // there's a valid refresh cookie" check for a reloaded, previously-authenticated tab; racing it
  // here would just fail identically for a truly anonymous visitor and — critically — escalate
  // into a force-logout redirect that makes no sense for someone who was never signed in. Return
  // null so the request goes out with no Authorization header; if the operation actually required
  // auth, the resulting 401 is handled reactively by errorLink via `forceRefresh: true` below.
  if (!options.forceRefresh && !current) {
    return null;
  }

  if (!inFlightRefresh) {
    inFlightRefresh = performRefresh().finally(() => {
      inFlightRefresh = null;
    });
  }

  return inFlightRefresh;
}
